import { useQuery } from '@tanstack/react-query';

import type { GetFinalizeDealInInventorySystemResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get finalize deal in inventory system status from a purchased vehicle
 */
export const getFinalizeDealInInventorySystem = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetFinalizeDealInInventorySystemResponse> => {
	const response = await apiClient.get<GetFinalizeDealInInventorySystemResponse>(
		ordersEndpoints.finalizeDealInInventorySystem(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting finalize deal in inventory system status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetFinalizeDealInInventorySystem = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.finalizeDealInInventorySystem.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error(
					'Unable to get finalize deal in inventory system status without an order ID.',
				);
			}
			return getFinalizeDealInInventorySystem(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
